/**
 * Bidirectional **visual ⇄ JS** conversion for a page action chain (ADR-0044
 * U-track, the Appsmith "edit the handler as code" model).
 *
 * Tray actions are a STRUCTURED `PageAction[]` (not a JS string like Appsmith),
 * executed by `lib/pageActions`. To give the operator Appsmith's "flip the
 * handler to JS and back" experience we serialize a chain to a sequence of
 * verb-SDK JS statements and parse it back:
 *
 *   visual:  [{type:"navigate", to:"/x"}, {type:"toast", message:"hi"}]
 *   JS:      navigate("/x");
 *            toast("hi");
 *
 * Conversion is **best-effort** (the owner's chosen scope): it covers the FLAT
 * LEAF chain (the 99% case — a sequence of simple actions). Control-flow
 * (`if`/`try`), per-action continuations (`on_success`/`on_failure`), a
 * per-action `confirm`, a JS-Object `run_js`, or an overlay carrying
 * context/on_close are "advanced" — `isSimpleLeafChain` returns false and the
 * caller keeps the visual editor (the JS toggle is hidden). On the parse side,
 * any statement the grammar doesn't recognise marks the result `lossy` so the
 * caller can keep the raw JS (stored as a single `run_js_inline` action) rather
 * than silently dropping it.
 *
 * The runtime executes a JS-mode handler via the `run_js_inline` action
 * (`pageActions`), running the code against the same verb SDK these statements
 * call — so a round-trip is behaviour-preserving.
 */
import type { PageAction } from "./pageActions";

/** verb name ⇄ action `type`, in the positional-arg order the verb takes. The
 *  SDK names mirror `pageActions.buildInlineJsSdk` (the runtime side). */
interface VerbSpec {
  verb: string;
  type: string;
  /** Positional scalar arg keys, in order. */
  args: string[];
  /** Optional trailing object-literal arg → this `values`-map key. */
  valuesKey?: string;
}

const VERBS: VerbSpec[] = [
  { verb: "navigate", type: "navigate", args: ["to"] },
  { verb: "toast", type: "toast", args: ["message"] },
  { verb: "refresh", type: "refresh", args: [] },
  { verb: "setVar", type: "set_variable", args: ["name", "value"] },
  { verb: "runQuery", type: "run_query", args: ["query"] },
  { verb: "printReport", type: "print_report", args: ["report"], valuesKey: "params" },
  { verb: "runWorkflow", type: "run_workflow", args: ["workflow"], valuesKey: "values" },
  { verb: "createRow", type: "create_row", args: ["table"], valuesKey: "values" },
  { verb: "updateRow", type: "update_row", args: ["table", "row"], valuesKey: "values" },
  { verb: "deleteRow", type: "delete_row", args: ["table", "row"] },
  { verb: "openDrawer", type: "open_drawer", args: ["page"] },
  { verb: "openOverlay", type: "open_overlay", args: ["page", "mode"] },
  { verb: "closeOverlay", type: "close_overlay", args: [] },
  { verb: "openModal", type: "open_modal", args: ["target"] },
  { verb: "closeModal", type: "close_modal", args: ["target"] },
  { verb: "storeValue", type: "global_value", args: ["key", "value"] },
  { verb: "removeValue", type: "global_remove", args: ["key"] },
  { verb: "clearStore", type: "global_clear", args: [] },
];

const BY_TYPE = new Map(VERBS.map((v) => [v.type, v]));
const BY_VERB = new Map(VERBS.map((v) => [v.verb, v]));

/** Is ONE action losslessly serialisable to JS? A known leaf verb, a `run_js`
 *  call (`Obj.fn(args)`), or `if`/`try` whose sub-chains are serialisable. A per-
 *  action `confirm` or an `on_success`/`on_failure` CONTINUATION can't be
 *  represented in the flat verb grammar, so it makes the chain non-convertible
 *  (visual-only); a `run_js` with an `assign` likewise. */
function actionSerializable(a: unknown): boolean {
  if (!a || typeof a !== "object") return false;
  const o = a as PageAction;
  if (o.confirm || o.on_success || o.on_failure) return false;
  if (o.type === "if") return chainSerializable(o.then) && chainSerializable(o.else);
  if (o.type === "try") return chainSerializable(o.do) && chainSerializable(o.catch);
  if (o.type === "run_js") return !o.assign;
  return BY_TYPE.has(o.type); // a known leaf verb
}

/** A (sub-)chain is serialisable iff every action is (an empty/None chain is). */
function chainSerializable(value: unknown): boolean {
  if (value == null) return true;
  if (!Array.isArray(value)) return false;
  return value.every(actionSerializable);
}

/** A chain the visual ⇄ JS toggle is offered for — losslessly serialisable to JS
 *  (leaves + `run_js` + `if`/`try`). The toggle-BACK may still be lossy (an
 *  `if`/`try` block isn't re-parsed into cards) but the toggle-TO never loses
 *  data, so the badge is safe to show. */
export function isConvertibleChain(value: unknown): boolean {
  return chainSerializable(value);
}

/** A chain that is exactly one `run_js_inline` — the raw-JS storage shape. */
export function isInlineJs(value: unknown): value is [PageAction] {
  return (
    Array.isArray(value) &&
    value.length === 1 &&
    !!value[0] &&
    typeof value[0] === "object" &&
    (value[0] as PageAction).type === "run_js_inline"
  );
}

// ---- serialize: visual → JS ------------------------------------------------

/** Render a `{{ }}` template down to its bare expression (Appsmith-pretty), else
 *  a JSON literal. A multi-segment / text+expr template stays a quoted string. */
function valueToJs(v: unknown): string {
  if (typeof v === "string") {
    const m = v.match(/^\s*\{\{([\s\S]*)\}\}\s*$/);
    if (m && !m[1].includes("{{")) return m[1].trim() || '""';
    return JSON.stringify(v);
  }
  return JSON.stringify(v ?? null);
}

/** A `{col: template}` values map → a JS object literal. */
function valuesToJs(values: unknown): string {
  if (!values || typeof values !== "object") return "{}";
  const parts = Object.entries(values as Record<string, unknown>).map(
    ([k, v]) => `${/^[A-Za-z_$][\w$]*$/.test(k) ? k : JSON.stringify(k)}: ${valueToJs(v)}`,
  );
  return `{ ${parts.join(", ")} }`;
}

/** Render a condition (`{{ }}` → bare expr, else a literal) for an `if`. */
function condToJs(v: unknown): string {
  return valueToJs(v) || "false";
}

/** Indent every non-empty line by two spaces (nested control-flow bodies). */
function indent(code: string): string {
  return code
    .split("\n")
    .map((l) => (l ? `  ${l}` : l))
    .join("\n");
}

/** Serialize ONE action to a JS statement (no trailing `;` for blocks). */
function actionToJs(a: PageAction): string {
  // run_js → a JS-Object call `Obj.fn(args)`.
  if (a.type === "run_js") {
    const fn = String(a.fn ?? "");
    const args =
      a.args && typeof a.args === "object" && Object.keys(a.args).length ? valuesToJs(a.args) : "";
    return `${fn}(${args})`;
  }
  // Control flow → real JS blocks (lossless; not re-parsed into cards on the way back).
  if (a.type === "if") {
    let s = `if (${condToJs(a.condition)}) {\n${indent(actionsToJs(a.then))}\n}`;
    if (Array.isArray(a.else) && a.else.length) s += ` else {\n${indent(actionsToJs(a.else))}\n}`;
    return s;
  }
  if (a.type === "try") {
    return `try {\n${indent(actionsToJs(a.do))}\n} catch (e) {\n${indent(actionsToJs(a.catch))}\n}`;
  }
  const spec = BY_TYPE.get(a.type);
  if (!spec) return `/* ${a.type} */`;
  const parts: string[] = [];
  // Positional scalar args — drop trailing empty/undefined ones for tidiness.
  for (const key of spec.args) parts.push(valueToJs(a[key]));
  while (parts.length && (parts[parts.length - 1] === '""' || parts[parts.length - 1] === "null")) {
    parts.pop();
  }
  // Trailing values map (only when non-empty).
  if (spec.valuesKey) {
    const vals = a[spec.valuesKey];
    if (vals && typeof vals === "object" && Object.keys(vals).length) parts.push(valuesToJs(vals));
  }
  return `${spec.verb}(${parts.join(", ")})`;
}

/** Serialize a simple-leaf chain to editable JS (one statement per line). */
export function actionsToJs(value: unknown): string {
  if (!Array.isArray(value)) return "";
  return value
    .filter((a) => a && typeof a === "object")
    .map((a) => {
      const js = actionToJs(a as PageAction);
      return js.endsWith("}") ? js : `${js};`; // no `;` after an if/try block
    })
    .join("\n");
}

// ---- parse: JS → visual ----------------------------------------------------

/** Split source into top-level statements on `;` / newline, respecting strings,
 *  template literals and nested `()[]{}`. */
function splitStatements(src: string): string[] {
  const out: string[] = [];
  let buf = "";
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quote) {
      buf += ch;
      if (ch === "\\") {
        buf += src[++i] ?? "";
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'" || ch === "`") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") depth--;
    if ((ch === ";" || ch === "\n") && depth === 0) {
      if (buf.trim()) out.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

/** Split a top-level comma list (args / object entries), quote- + bracket-aware. */
function splitTop(src: string): string[] {
  const out: string[] = [];
  let buf = "";
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quote) {
      buf += ch;
      if (ch === "\\") buf += src[++i] ?? "";
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'" || ch === "`") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") depth--;
    if (ch === "," && depth === 0) {
      out.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

/** Classify a JS arg token → a PageAction value. A quoted string / number /
 *  bool / null becomes the literal; anything else (an identifier / member /
 *  expression like `$page.x`) is re-wrapped as a `{{ }}` template. */
function jsToValue(token: string): unknown {
  const t = token.trim();
  if (/^(['"`]).*\1$/s.test(t)) {
    try {
      // A plain quoted string → its content (handles escapes).
      if (t[0] !== "`") return JSON.parse(t[0] === "'" ? `"${t.slice(1, -1).replace(/"/g, '\\"')}"` : t);
      return t.slice(1, -1); // template literal w/o interpolation
    } catch {
      return t.slice(1, -1);
    }
  }
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  if (t === "true") return true;
  if (t === "false") return false;
  if (t === "null") return null;
  return `{{ ${t} }}`; // an expression → a binding
}

/** Parse a `{ k: v, … }` object-literal arg → a values map (best-effort). */
function jsToValues(token: string): Record<string, unknown> | null {
  const t = token.trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  const out: Record<string, unknown> = {};
  for (const entry of splitTop(t.slice(1, -1))) {
    const colon = topLevelColon(entry);
    if (colon < 0) return null;
    let key = entry.slice(0, colon).trim();
    if (/^(['"`]).*\1$/s.test(key)) key = key.slice(1, -1);
    if (!key) return null;
    out[key] = jsToValue(entry.slice(colon + 1));
  }
  return out;
}

/** Index of the top-level `:` in an object entry (quote/bracket-aware). */
function topLevelColon(src: string): number {
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quote) {
      if (ch === "\\") i++;
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'" || ch === "`") quote = ch;
    else if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") depth--;
    else if (ch === ":" && depth === 0) return i;
  }
  return -1;
}

export interface JsToActionsResult {
  actions: PageAction[];
  /** A statement the grammar couldn't map (control-flow, an unknown verb, a bad
   *  call) — the caller should keep the raw JS rather than the (partial) chain. */
  lossy: boolean;
}

/**
 * Parse verb-SDK JS back into a structured chain. Each statement must be a
 * recognised `verb(args)` call; anything else (a comment, a control-flow block,
 * an unknown function) sets `lossy = true`. Empty input → an empty chain.
 *
 * `opts.queryNames` lets a `<Query>.run()` call parse back to a `run_query`
 * action (the Appsmith `<Query>.run()` shape) instead of a `run_js`.
 */
export function jsToActions(src: string, opts?: { queryNames?: string[] }): JsToActionsResult {
  const queryNames = new Set(opts?.queryNames ?? []);
  const actions: PageAction[] = [];
  let lossy = false;
  for (const stmt of splitStatements(src)) {
    // A call whose callee may be dotted (`Obj.fn`) — a member call that ISN'T a
    // known bare verb is a `run_js` JS-Object handler.
    const m = stmt.match(/^([A-Za-z_$][\w$.]*)\s*\(([\s\S]*)\)$/);
    if (!m) {
      lossy = true;
      continue;
    }
    const callee = m[1];
    const spec = BY_VERB.get(callee);
    if (!spec) {
      // `<Query>.run()` (no args) → a run_query, when the root is a declared query.
      const runMatch = callee.match(/^([A-Za-z_$][\w$]*)\.run$/);
      if (runMatch && queryNames.has(runMatch[1]) && !m[2].trim()) {
        actions.push({ type: "run_query", query: runMatch[1] });
        continue;
      }
      if (callee.includes(".")) {
        // run_js: `Obj.fn()` or `Obj.fn({ … })` — zero args or one object-literal.
        const rawArgs = m[2].trim() ? splitTop(m[2]) : [];
        const action: PageAction = { type: "run_js", fn: callee };
        if (rawArgs.length === 1) {
          const vals = jsToValues(rawArgs[0]);
          if (vals === null) {
            lossy = true;
            continue;
          }
          action.args = vals;
        } else if (rawArgs.length > 1) {
          lossy = true;
          continue;
        }
        actions.push(action);
        continue;
      }
      lossy = true;
      continue;
    }
    const rawArgs = m[2].trim() ? splitTop(m[2]) : [];
    const action: PageAction = { type: spec.type };
    let ai = 0;
    // Positional scalar args.
    for (const key of spec.args) {
      if (ai < rawArgs.length && !(spec.valuesKey && ai === rawArgs.length - 1 && rawArgs[ai].trim().startsWith("{"))) {
        action[key] = jsToValue(rawArgs[ai]);
        ai++;
      }
    }
    // Trailing values object.
    if (spec.valuesKey && ai < rawArgs.length) {
      const vals = jsToValues(rawArgs[ai]);
      if (vals === null) {
        lossy = true;
        continue;
      }
      action[spec.valuesKey] = vals;
      ai++;
    }
    if (ai < rawArgs.length) {
      lossy = true; // extra args we can't place
      continue;
    }
    actions.push(action);
  }
  return { actions, lossy };
}
